import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import {
  ExButton,
  ExInput,
  PASSWORD_REGEX,
  RULES_PATTERN_EMAIL,
  RULES_PATTERN_PHONE_NUMBER,
  RULES_PHONE_NUMBER_MAX_LENGTH,
  RULES_REQUIERD,
  useResizeObserver,
  useToastMessage,
} from "@energyx-dev/energyx-ui";
import { useMutation } from "@tanstack/react-query";
import {
  getDuplicatedCheckBusinessLicenseNumber,
  getDuplicatedCheckPhoneNumberOrEmail,
  postSignup,
  sendVerification,
} from "api/signup";
import { getTermsLatest } from "api/terms";
import { ErrorCaption } from "components/common/ErrorCaption";
import { SignUpTerms } from "components/signup/SignUpTerms";
import { convertSignUpInputNameToHackleKey } from "components/signup/helper";
import { DEVICE } from "constants/device";
import {
  EN_RULES_PATTERN_EMAIL,
  EN_RULES_PATTERN_PHONE_NUMBER,
  EN_RULES_PHONE_NUMBER_MAX_LENGTH,
  EN_RULES_REQUIERD,
} from "constants/rules";
import { VERIFICATION_STATUS } from "constants/verification";
import { hackleEventUtil } from "hackle/hackleEventUtil";
import { useFetchSSE } from "hooks/useFetchSSE";
import { useFile } from "hooks/useFile";
import useTranslation from "hooks/useTranslation";
import { ElementRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { TReturnUpload } from "types/commonTypes";
import { THackleFileProps, THackleMemberTypeProps } from "types/hackle";
import { TCorporationSignUp, TVerificationSseRes } from "types/signup/signUpTypes";
import { checkBusinessLicenseNumber, removeHyphen } from "utils/formatters";
import { checkFilesType } from "utils/utils";

export const Corporation = ({
  signUpCompletedRedirect,
}: {
  signUpCompletedRedirect: () => void;
}) => {
  const theme = useTheme();
  const device = useResizeObserver();
  const methods = useForm<TCorporationSignUp & { re_password: string }>({
    mode: "onTouched",
    defaultValues: {
      signUpTerms: false,
      privacyPolicyTerms: false,
      marketing: false,
    },
  });
  const {
    getValues,
    setError,
    trigger,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = methods;
  const { upload } = useFile();
  const isDesktop = device === DEVICE.DESKTOP;
  const fileRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<ElementRef<typeof ExInput>>(null);
  const phoneNumberInputRef = useRef<ElementRef<typeof ExInput>>(null);
  const prevPhoneNumberRef = useRef<string>();
  const prevEmailRef = useRef<string>();
  const isDuplicatedBusinessLicenseNumberRef = useRef(false);
  const [phoneNumberVerificationResult, setPhoneNumberVerificationResult] =
    useState<TVerificationSseRes>({
      verified: false,
      code: "",
    });
  const [emailVerificationResult, setEmailVerificationResult] = useState<TVerificationSseRes>({
    verified: false,
    code: "",
  });
  const [phoneNumberVerified, setPhoneNumberVerified] = useState(VERIFICATION_STATUS.READY);
  const [emailVerified, setEmailVerified] = useState(VERIFICATION_STATUS.READY);
  const isEnglishPage = useTranslation();
  const { fetchSse } = useFetchSSE();
  const { showToastMsg } = useToastMessage();
  const { mutateAsync: postSignUp } = useMutation(postSignup, {
    onSuccess: () => signUpCompletedRedirect(),
  });
  const { mutateAsync, isLoading } = useMutation(sendVerification, {
    onSuccess: async (data, variables) => {
      if (data === 409) {
        if (variables.type === "sms") {
          setError("managerPhoneNumber", {
            message: isEnglishPage
              ? "This mobile number has already been registered."
              : "이미 가입하신 휴대폰 번호입니다.",
          });
        } else {
          setError("email", {
            message: isEnglishPage
              ? "This email address has already been registered."
              : "이미 가입하신 이메일 주소입니다.",
          });
        }
        return;
      }

      if (variables.type === "sms") {
        showToastMsg({
          status: "normal",
          type: "single-none",
          message: isEnglishPage
            ? "Verification message has been sent."
            : "인증 문자를 전송했습니다.",
        });
        phoneNumberInputRef.current?.timerStart();
        setPhoneNumberVerified(VERIFICATION_STATUS.REQUEST);
      } else {
        showToastMsg({
          status: "normal",
          type: "single-none",
          message: isEnglishPage
            ? "Verification email has been sent."
            : "인증 이메일을 전송했습니다.",
        });
        emailInputRef.current?.timerStart();
        setEmailVerified(VERIFICATION_STATUS.REQUEST);
      }

      await fetchSse({
        memberType: "corporation",
        to: `${variables.type === "sms" ? "sms" : "email"}`,
        ...(variables.type === "sms"
          ? { phoneNumber: removeHyphen(getValues("managerPhoneNumber")) }
          : { email: getValues("email") }),
        verificationId: data,
        setResult:
          variables.type === "sms" ? setPhoneNumberVerificationResult : setEmailVerificationResult,
      });
    },
  });

  const handleOnClickUpload = () => {
    fileRef.current?.click();
  };

  const handleOnFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!checkFilesType(files[0])) {
      setError("businessLicenseFileName", {
        message: "파일의 형식이 맞지 않습니다.",
      });
      return;
    }
    await upload<TReturnUpload>({
      file: files[0],
      category: "corporation",
      identity: "license",
    }).then((res) => {
      const { id, originName } = res;
      hackleEventUtil<THackleFileProps>({
        key: "change_business_license_file",
        properties: { file_id: id, file_name: originName },
      });
      setValue("businessLicenseFileName", originName);
      setValue("businessLicenseFileId", id);
      clearErrors("businessLicenseFileName");
    });
  };

  const handleOnClickVerification = async (type: "email" | "managerPhoneNumber") => {
    if (type === "email") {
      if (!(await trigger("email"))) return;
      const isDuplicated = await getDuplicatedCheckPhoneNumberOrEmail({
        type: "corporation",
        email: getValues("email"),
      });
      if (isDuplicated) {
        setError("email", {
          message: isEnglishPage
            ? "This email address has already been registered."
            : "이미 가입하신 이메일 주소입니다.",
        });
        return;
      }
      if (isLoading) return;
      hackleEventUtil<THackleMemberTypeProps>({
        key: "click_signUp_request_auth_email",
        properties: { member_type: "법인" },
      });
      mutateAsync({
        memberType: "corporation",
        type: "email",
        email: getValues("email"),
      });
      prevEmailRef.current = getValues("email");
    } else {
      if (!(await trigger("managerPhoneNumber"))) return;
      if (removeHyphen(getValues("managerPhoneNumber"))?.length > 9) {
        const isDuplicated = await getDuplicatedCheckPhoneNumberOrEmail({
          type: "corporation",
          phoneNumber: removeHyphen(getValues("managerPhoneNumber")),
        });
        if (isDuplicated) {
          setError("managerPhoneNumber", {
            message: isEnglishPage
              ? "This mobile number has already been registered."
              : "이미 가입하신 휴대폰 번호입니다.",
          });
          return;
        }
      }
      hackleEventUtil<THackleMemberTypeProps>({
        key: "click_signUp_request_auth_phone_number",
        properties: { member_type: "법인" },
      });
      mutateAsync({
        memberType: "corporation",
        type: "sms",
        phoneNumber: removeHyphen(getValues("managerPhoneNumber")),
      });
      prevPhoneNumberRef.current = removeHyphen(getValues("managerPhoneNumber"));
    }
  };

  const onSubmit = async () => {
    if (!(await trigger())) {
      if (
        (await trigger("managerPhoneNumber")) &&
        phoneNumberVerified !== VERIFICATION_STATUS.COMPLETE
      ) {
        setError("managerPhoneNumber", {
          message: isEnglishPage
            ? " Please click the link in the verification message."
            : "인증요청 후, 전송된 문자의 링크를 클릭해주세요.",
        });
      }
      if ((await trigger("email")) && emailVerified !== VERIFICATION_STATUS.COMPLETE) {
        setError("email", {
          message: isEnglishPage
            ? " Please click the 'Verify Email' button in the sent email."
            : "인증요청 후, 전송된 메일의 '이메일 인증하기' 버튼을 클릭해주세요.",
        });
      }

      return;
    }

    if (
      ((await trigger("managerPhoneNumber")) &&
        phoneNumberVerified !== VERIFICATION_STATUS.COMPLETE) ||
      ((await trigger("email")) && emailVerified !== VERIFICATION_STATUS.COMPLETE)
    ) {
      if (
        (await trigger("managerPhoneNumber")) &&
        phoneNumberVerified !== VERIFICATION_STATUS.COMPLETE
      ) {
        setError("managerPhoneNumber", {
          message: isEnglishPage
            ? " Please click the link in the verification message."
            : "인증요청 후, 전송된 문자의 링크를 클릭해주세요.",
        });
      }
      if ((await trigger("email")) && emailVerified !== VERIFICATION_STATUS.COMPLETE) {
        setError("email", {
          message: isEnglishPage
            ? " Please click the 'Verify Email' button in the sent email."
            : "인증요청 후, 전송된 메일의 '이메일 인증하기' 버튼을 클릭해주세요.",
        });
      }

      return;
    }

    if (isDuplicatedBusinessLicenseNumberRef.current) {
      setError("businessLicenseNumber", {
        message: isEnglishPage
          ? "This business license has already been registered."
          : "이미 가입하신 사업자등록번호입니다.",
      });
      return;
    }
    if (!(await trigger())) return;

    const urlParameter = window.document.referrer;
    const fetchfrom = urlParameter.includes("tomarch") ? "TOMARCH" : "ENERGYX";
    const { id: joinTermsId } = await getTermsLatest("JOIN", "ENERGYX");
    const { id: privacyPolicyTermsId } = await getTermsLatest("PRIVACY_POLICY", fetchfrom);

    const {
      businessLicenseFileId,
      businessLicenseFileName,
      businessLicenseNumber,
      companyName,
      email,
      marketing,
      managerName,
      managerPhoneNumber,
      password,
    } = getValues();
    postSignUp({
      memberType: "corporation",
      corporationParams: {
        businessLicenseFileId,
        businessLicenseFileName,
        businessLicenseNumber: removeHyphen(businessLicenseNumber),
        companyName,
        email,
        emailVerifiedTokenId: emailVerificationResult.emailVerifiedTokenId as string,
        marketing,
        joinTermsId,
        managerName,
        managerPhoneNumber: removeHyphen(managerPhoneNumber),
        password,
        smsVerifiedTokenId: phoneNumberVerificationResult.smsVerifiedTokenId as string,
        privacyPolicyTermsId,
      },
    });
  };

  const handleOnChangeEmail = () => {
    if (emailVerified === VERIFICATION_STATUS.REQUEST) {
      emailInputRef.current?.timerInit();
      setEmailVerified(VERIFICATION_STATUS.READY);
    }
  };

  const handleOnChangePhoneNumber = () => {
    if (phoneNumberVerified === VERIFICATION_STATUS.REQUEST) {
      phoneNumberInputRef.current?.timerInit();
      setPhoneNumberVerified(VERIFICATION_STATUS.READY);
    }
  };

  const handleOnBlur = async (
    type:
      | "email"
      | "managerPhoneNumber"
      | "businessLicenseNumber"
      | "password"
      | "re_password"
      | "companyName"
      | "managerName"
  ) => {
    if (!(await trigger([type]))) return;

    hackleEventUtil<THackleMemberTypeProps>({
      key: convertSignUpInputNameToHackleKey(type),
      properties: { member_type: "법인" },
    });
    let isDuplicated = false;

    switch (type) {
      case "managerPhoneNumber":
        // if (
        //   (errors.managerPhoneNumber &&
        //     (errors.managerPhoneNumber.type === "required" ||
        //       errors.managerPhoneNumber.type === "pattern")) ||
        //   !(removeHyphen(getValues("managerPhoneNumber"))?.length > 9)
        // ) {
        //   return;
        // }

        isDuplicated = await getDuplicatedCheckPhoneNumberOrEmail({
          type: "corporation",
          phoneNumber: removeHyphen(getValues("managerPhoneNumber")),
        });
        if (isDuplicated) {
          setError("managerPhoneNumber", {
            message: isEnglishPage
              ? "This mobile number has already been registered."
              : "이미 가입하신 휴대폰 번호입니다.",
          });
        }
        break;
      case "email":
        // if (
        //   (errors.email && (errors.email.type === "required" || errors.email.type === "pattern")) ||
        //   !getValues("email")
        // ) {
        //   return;
        // }
        isDuplicated = await getDuplicatedCheckPhoneNumberOrEmail({
          type: "corporation",
          email: getValues("email"),
        });
        if (isDuplicated) {
          setError("email", {
            message: isEnglishPage
              ? "This email address has already been registered."
              : "이미 가입하신 이메일 주소입니다.",
          });
        }
        break;
      default:
        // if (
        //   (errors.businessLicenseNumber &&
        //     (errors.businessLicenseNumber.type === "required" ||
        //       errors.businessLicenseNumber?.type === "validate")) ||
        //   !(removeHyphen(getValues("businessLicenseNumber"))?.length === 10)
        // ) {
        //   return;
        // }
        if (type === "businessLicenseNumber") {
          isDuplicated = await getDuplicatedCheckBusinessLicenseNumber(
            removeHyphen(getValues("businessLicenseNumber"))
          );
          if (isDuplicated) {
            setError("businessLicenseNumber", {
              message: isEnglishPage
                ? "This business license has already been registered."
                : "이미 가입하신 사업자등록번호입니다.",
            });
            // if (!(await trigger())) return;
            isDuplicatedBusinessLicenseNumberRef.current = true;
          } else {
            isDuplicatedBusinessLicenseNumberRef.current = false;
          }
        }
    }
  };

  useEffect(() => {
    if (phoneNumberVerificationResult.code) {
      phoneNumberInputRef.current?.timerInit();
      hackleEventUtil<THackleMemberTypeProps>({
        key: "success_signUp_auth_phone_number",
        properties: { member_type: "법인" },
      });
      setPhoneNumberVerified(VERIFICATION_STATUS.COMPLETE);
      clearErrors("managerPhoneNumber");
    }
    if (emailVerificationResult.code) {
      emailInputRef.current?.timerInit();
      hackleEventUtil<THackleMemberTypeProps>({
        key: "success_signUp_auth_email",
        properties: { member_type: "법인" },
      });
      setEmailVerified(VERIFICATION_STATUS.COMPLETE);
      clearErrors("email");
    }
  }, [emailVerificationResult, phoneNumberVerificationResult]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <SignupForm emailVerified={false} phoneNumberVerified={false}>
          <section className="signup-input">
            <span>{isEnglishPage ? "Account Information" : "계정 정보"}</span>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              <InputSetWrapper isDesktop={isDesktop}>
                <ExInput
                  type={"input"}
                  name={"email"}
                  inputSize={isDesktop ? "desktop" : "mobile"}
                  label={isEnglishPage ? "E-mail" : "이메일 주소"}
                  placeholder={isEnglishPage ? "E-mail" : "이메일 주소"}
                  rules={
                    isEnglishPage
                      ? {
                          ...EN_RULES_REQUIERD,
                          ...EN_RULES_PATTERN_EMAIL,
                        }
                      : {
                          ...RULES_REQUIERD,
                          ...RULES_PATTERN_EMAIL,
                        }
                  }
                  callbackOnBlur={() => handleOnBlur("email")}
                  hasRulesButNoErrorCaption
                  callbackOnChange={handleOnChangeEmail}
                  readonly={emailVerified === VERIFICATION_STATUS.COMPLETE}
                  ref={emailInputRef}
                  timerAdornment={900}
                />
                <ExButton
                  variant={"contained"}
                  size={"small"}
                  {...(isDesktop ? { width: 86 } : { width: 84 })}
                  label={
                    emailVerified === VERIFICATION_STATUS.READY
                      ? isEnglishPage
                        ? "Verify"
                        : "인증 요청"
                      : emailVerified === VERIFICATION_STATUS.REQUEST
                      ? isEnglishPage
                        ? "Re-request"
                        : "재요청"
                      : isEnglishPage
                      ? "Verified"
                      : "인증 완료"
                  }
                  backgroundColor={
                    emailVerified === VERIFICATION_STATUS.COMPLETE
                      ? theme.color.green[10]
                      : undefined
                  }
                  labelColor={
                    emailVerified === VERIFICATION_STATUS.COMPLETE
                      ? theme.color.green[60]
                      : undefined
                  }
                  onClick={() => handleOnClickVerification("email")}
                  className="signup-corporation-emailauthrequest"
                />
              </InputSetWrapper>

              {errors.email && <ErrorCaption message={errors.email.message as string} />}
            </div>
            <ExInput
              type={"input"}
              name={"password"}
              inputSize={isDesktop ? "desktop" : "mobile"}
              timerFinishedMessage={isEnglishPage ? "Your verification time has expired." : ""}
              label={isEnglishPage ? "Password" : "비밀번호"}
              placeholder={isEnglishPage ? "Password" : "비밀번호"}
              caption={
                isEnglishPage
                  ? "At least 6 characters using a combination of alphanumeric characters."
                  : "영문과 숫자를 조합하여 6자 이상 입력해 주세요."
              }
              callbackOnBlur={() => handleOnBlur("password")}
              password={true}
              rules={
                isEnglishPage
                  ? {
                      ...EN_RULES_REQUIERD,
                      pattern: {
                        value: PASSWORD_REGEX,
                        message:
                          "At least 6 characters using a combination of alphanumeric characters.",
                      },
                    }
                  : {
                      ...RULES_REQUIERD,
                      pattern: {
                        value: PASSWORD_REGEX,
                        message: "영문과 숫자를 조합하여 6자 이상 입력해주세요.",
                      },
                    }
              }
            />
            <ExInput
              type="input"
              name="re_password"
              label={isEnglishPage ? "Confirm Password" : "비밀번호 확인"}
              inputSize={isDesktop ? "desktop" : "mobile"}
              placeholder={isEnglishPage ? "Confirm Password" : "비밀번호 확인"}
              caption={
                isEnglishPage
                  ? "Please enter your password again."
                  : "비밀번호를 한번 더 입력해 주세요."
              }
              callbackOnBlur={() => handleOnBlur("re_password")}
              rules={
                isEnglishPage
                  ? {
                      ...EN_RULES_REQUIERD,
                      validate: (value) =>
                        getValues("password") === value || "Please re-enter the same password.",
                    }
                  : {
                      ...RULES_REQUIERD,
                      validate: (value) =>
                        getValues("password") === value || "동일한 비밀번호를 입력해 주세요.",
                    }
              }
              password={true}
            />
          </section>
          <section className="signup-input">
            <span>{isEnglishPage ? "Business Information" : "법인 정보"}</span>
            <ExInput
              type={"input"}
              name={"companyName"}
              inputSize={isDesktop ? "desktop" : "mobile"}
              callbackOnBlur={() => handleOnBlur("companyName")}
              label={isEnglishPage ? "Business Name" : "상호"}
              placeholder={isEnglishPage ? "Business Name" : "상호"}
              caption={
                isEnglishPage
                  ? "Fill in the business name according to the business license."
                  : "사업자등록증상의 상호를 입력해 주세요."
              }
              rules={isEnglishPage ? EN_RULES_REQUIERD : RULES_REQUIERD}
            />
            <ExInput
              type={"input"}
              name={"businessLicenseNumber"}
              inputSize={isDesktop ? "desktop" : "mobile"}
              label={isEnglishPage ? "Business License Number" : "사업자등록번호"}
              placeholder={isEnglishPage ? "Business License Number" : "사업자등록번호"}
              rules={
                isEnglishPage
                  ? {
                      ...EN_RULES_REQUIERD,
                      maxLength: {
                        value: 12,
                        message: "Please confirm your business license number.",
                      },
                      validate: (value) =>
                        checkBusinessLicenseNumber(value.replace(/-/gi, "")) ||
                        "Please confirm your business license number.",
                    }
                  : {
                      ...RULES_REQUIERD,
                      maxLength: {
                        value: 12,
                        message: "사업자등록번호를 확인해 주세요.",
                      },
                      validate: (value) =>
                        checkBusinessLicenseNumber(value.replace(/-/gi, "")) ||
                        "사업자등록번호를 확인해 주세요.",
                    }
              }
              businessAutoHyphen={true}
              callbackOnBlur={() => handleOnBlur("businessLicenseNumber")}
            />
            <InputSetWrapper isDesktop={isDesktop}>
              <ExInput
                type={"input"}
                name={"businessLicenseFileName"}
                inputSize={isDesktop ? "desktop" : "mobile"}
                label={isEnglishPage ? "Business License" : "사업자등록증"}
                placeholder={isEnglishPage ? "Business License" : "사업자등록증"}
                rules={isEnglishPage ? EN_RULES_REQUIERD : RULES_REQUIERD}
                leftIconName={""}
                caption={
                  isEnglishPage
                    ? "Upload file in PDF, JPG, JPEG, PNG format."
                    : "PDF, JPG, JPEG, PNG 형식의 파일을 업로드해 주세요."
                }
                readonly
              />
              <ExButton
                variant={"contained"}
                size={"small"}
                {...(isDesktop ? { width: 86 } : { width: 84 })}
                label={isEnglishPage ? "Upload File" : "파일 선택"}
                onClick={handleOnClickUpload}
                className="signup-corporation-fileselect"
              />
              <input
                type="file"
                hidden
                multiple={false}
                accept="application/pdf,image/jpg,image/jpeg,image/png"
                ref={fileRef}
                onChange={(e) => handleOnFileChange(e.target.files)}
              />
            </InputSetWrapper>
          </section>
          <section className="signup-input">
            <span>{isEnglishPage ? "Person in Charge" : "담당자 정보"}</span>
            <ExInput
              type={"input"}
              name={"managerName"}
              inputSize={isDesktop ? "desktop" : "mobile"}
              label={isEnglishPage ? "Full Name" : "담당자 성명"}
              callbackOnBlur={() => handleOnBlur("managerName")}
              placeholder={isEnglishPage ? "Full Name" : "담당자 성명"}
              rules={
                isEnglishPage
                  ? {
                      required: {
                        value: true,
                        message: "Please complete this required field.",
                      },
                    }
                  : {
                      required: {
                        value: true,
                        message: "필수 입력 항목입니다.",
                      },
                    }
              }
            />
            <InputSetWrapper isDesktop={isDesktop}>
              <ExInput
                type={"input"}
                name={"managerPhoneNumber"}
                inputSize={isDesktop ? "desktop" : "mobile"}
                timerFinishedMessage={isEnglishPage ? "Your verification time has expired." : ""}
                label={isEnglishPage ? "Contact(Mobile)" : "담당자 휴대폰 번호"}
                placeholder={isEnglishPage ? "Contact(Mobile)" : "담당자 휴대폰 번호"}
                readonly={phoneNumberVerified === VERIFICATION_STATUS.COMPLETE}
                rules={
                  isEnglishPage
                    ? {
                        ...EN_RULES_REQUIERD,
                        ...EN_RULES_PATTERN_PHONE_NUMBER,
                        ...EN_RULES_PHONE_NUMBER_MAX_LENGTH,
                      }
                    : {
                        ...RULES_REQUIERD,
                        ...RULES_PATTERN_PHONE_NUMBER,
                        ...RULES_PHONE_NUMBER_MAX_LENGTH,
                      }
                }
                callbackOnBlur={() => handleOnBlur("managerPhoneNumber")}
                callbackOnChange={handleOnChangePhoneNumber}
                phoneNumAutoHyphen={true}
                ref={phoneNumberInputRef}
                timerAdornment={300}
              />
              <ExButton
                variant="contained"
                size="small"
                {...(isDesktop ? { width: 86 } : { width: 84 })}
                label={
                  phoneNumberVerified === VERIFICATION_STATUS.READY
                    ? isEnglishPage
                      ? "Verify"
                      : "인증 요청"
                    : phoneNumberVerified === VERIFICATION_STATUS.REQUEST
                    ? isEnglishPage
                      ? "Re-request"
                      : "재요청"
                    : isEnglishPage
                    ? "Verified"
                    : "인증 완료"
                }
                backgroundColor={
                  phoneNumberVerified === VERIFICATION_STATUS.COMPLETE
                    ? theme.color.green[10]
                    : undefined
                }
                labelColor={
                  phoneNumberVerified === VERIFICATION_STATUS.COMPLETE
                    ? theme.color.green[60]
                    : undefined
                }
                onClick={() => handleOnClickVerification("managerPhoneNumber")}
                className=" signup-corporation-phoneauthrequest"
              />
            </InputSetWrapper>
          </section>
          <SignUpTerms signupDivision="corporation" className="signup-corporation-alltermsagree" />
          <ExButton
            variant={"contained"}
            size={isDesktop ? "xLarge" : "large"}
            label={isEnglishPage ? "Join" : "회원가입 완료"}
            onClick={onSubmit}
            backgroundColor={isEnglishPage ? "#2F2F30" : ""}
            className="signup-normal-signupcomplete"
          />
        </SignupForm>
      </form>
    </FormProvider>
  );
};
const SignupForm = styled.div<{
  emailVerified: boolean;
  phoneNumberVerified: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 32px;

  .signup-input {
    display: flex;
    flex-direction: column;
    gap: 16px;

    > span {
      ${(props) => props.theme.font.Subtitle2}
    }

    .phone-number-input-button {
      button {
        align-self: start;
        margin-top: 12px;
        ${(props) =>
          props.phoneNumberVerified && {
            backgroundColor: props.theme.color.green[10],
            label: {
              color: props.theme.color.green[60],
            },
          }}
      }
    }

    .business-license-input-button {
      button {
        align-self: start;
        margin-top: 12px;
      }
    }
  }
`;

const InputSetWrapper = styled.div<{ isDesktop: boolean }>`
  display: flex;
  gap: 24px;
  align-items: center;

  > div:first-of-type {
    flex: 1;
  }

  > div:last-of-type {
    align-self: start;
    margin-top: ${({ isDesktop }) => (isDesktop ? "12px" : "6px")};
  }

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    gap: 16px;
  }
`;
